"use client";

import { createContext, useContext, type ReactNode } from "react";
import { PITCHES, type Pitch } from "./pitch";

/**
 * Argumentaire retenu pour ce chargement de page.
 *
 * POURQUOI UN CONTEXTE ALIMENTÉ PAR LE SERVEUR, et pas un simple tirage dans le
 * composant : tirer au sort pendant le rendu casserait la règle de pureté de
 * React, et tirer côté navigateur ferait diverger le HTML envoyé de celui
 * recalculé — l'hydratation remplacerait alors l'argumentaire sous les yeux du
 * visiteur, à chaque chargement, ce qui se voit et fait bon marché.
 *
 * Le tirage a donc lieu UNE fois par requête, dans le `layout` serveur des
 * écrans concernés. Deux conséquences heureuses : aucune divergence possible,
 * et le panneau ne change PAS d'un écran à l'autre sous le même layout, qui ne
 * se réexécute pas. C'est bien ce qu'on veut : un argumentaire qui sauterait
 * en cours de parcours donnerait l'impression d'avoir changé de site.
 */
const PitchContext = createContext<number>(0);

export function PitchProvider({
  index,
  children,
}: {
  index: number;
  children: ReactNode;
}) {
  return <PitchContext.Provider value={index}>{children}</PitchContext.Provider>;
}

export function usePitch(): { pitch: Pitch; index: number } {
  const raw = useContext(PitchContext);
  // Repli défensif : un écran monté hors du provider (ou un index hors bornes
  // après retrait d'un argumentaire) doit afficher quelque chose, pas planter.
  const index = Number.isInteger(raw) && raw >= 0 && raw < PITCHES.length ? raw : 0;
  return { pitch: PITCHES[index], index };
}
